const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * App routes that are a single URL segment and must not be treated as public profile handles.
 * Keeps /creations, /contribute, etc. from matching /:username when no static route exists.
 */
export const RESERVED_TOP_LEVEL_SEGMENTS = new Set([
  'news',
  'explore',
  'creation-detail',
  'user-profile',
  'signup',
  'login',
  'register',
  'invite',
  'send-invite',
  'send-recover',
  'recover-password',
  'terms',
  'legal-notice',
  'cookies',
  'privacy',
  'error',
  'admin',
  'creations',
  'contribute',
  'profiles',
  'api',
  'assets',
  'pdfjs',
  'library',
  'search',
  'chat',
  'conversations',
  'orders',
  'shipments',
  'profile',
  'my-profile',
  'notifications',
  'on-boarding',
  'collection',
  'edit-networks',
  'payments-history',
  'withdrawals',
  'earnings',
  'creator-analytics',
  'shipping-addresses',
  'shipping-methods',
  'complete-profile',
  'edit-profile',
  'invite-community',
  'invite-contacts',
  'change-password',
  'add-social-links',
]);

/**
 * Whether the segment is a profile UUID (legacy /profiles/:uuid paths).
 * @param {string | undefined | null} segment
 */
export function isProfileUuid(segment) {
  return Boolean(segment && UUID_REGEX.test(String(segment)));
}

/**
 * Normalize a handle for comparison (strip leading @, trim, lowercase).
 * @param {string | undefined | null} handle
 */
export function normalizeProfileHandle(handle) {
  return String(handle || '')
    .replace(/^@/, '')
    .trim()
    .toLowerCase();
}

/**
 * Valid public handle for /username URLs: matches signup rules, not a reserved app path.
 * @param {string | undefined | null} segment
 */
export function isLikelyUsernamePathSegment(segment) {
  if (!segment || typeof segment !== 'string') return false;
  const s = segment.trim();
  if (!/^[a-zA-Z0-9_.]{3,30}$/.test(s)) return false;
  if (RESERVED_TOP_LEVEL_SEGMENTS.has(s.toLowerCase())) return false;
  return true;
}

/**
 * Single-segment profile URLs like /BATNAUSER (for layout / bottom nav).
 * @param {string} pathname
 */
export function isPublicProfilePathname(pathname) {
  const m = pathname.match(/^\/([^/]+)$/);
  if (!m) return false;
  const seg = m[1];
  return isProfileUuid(seg) || isLikelyUsernamePathSegment(seg);
}

/**
 * Public path for a profile: /handle when userName exists, otherwise /profiles/{uuid}.
 * @param {{ userName?: string; user_id?: string } | null | undefined} user
 * @returns {string | null}
 */
export function getProfilePublicPath(user) {
  if (!user) return null;
  const raw = user.userName || user.username;
  const handle = raw ? String(raw).replace(/^@/, '').trim() : '';
  if (handle) {
    return `/${encodeURIComponent(handle)}`;
  }
  if (user.user_id) {
    return `/profiles/${user.user_id}`;
  }
  return null;
}

/**
 * Absolute share URL for the current origin (browser only).
 * @param {{ userName?: string; user_id?: string } | null | undefined} user
 */
export function getProfileShareUrl(user) {
  if (typeof window === 'undefined') return '';
  const path = getProfilePublicPath(user);
  if (!path) return window.location.origin;
  return `${window.location.origin}${path}`;
}
